import { useMutation } from '@tanstack/react-query';
import React from 'react';

import { CreateDroneHarmonyIntegrationParameters } from '../../../../../../../typings/api/skymap/rest/v1/project';
import { SkyMapAxiosServiceFactory } from '../../../../../services/axios/skymap-axios-service-factory';
import { RequestParamsToApiPaths } from '../../../../../utils/typescript-utils';
import { errorUtils } from '../../../../api/error-handling';
import { useErrorHandling } from '../../../../hooks/use-error-handling';
import { useReloadPage } from '../../../../hooks/use-reload-page';
import { ProjectContext } from '../../../../state/project-state/project-state';
import { Button } from '../../../button/button';
import { InfoBox } from '../../../info-box/info-box';
import { Stack } from '../../../stack/stack';
import { IntegrationTitle } from '../../integration-title';
import {
  buildActiveDroneHarmonyIntegrationFromApiResponse,
  DroneHarmonyIntegrationItem,
  IntegrationsContext,
  IntegrationStatus,
} from '../../state/integrations-state';
import { useRemoveIntegrationQuery } from './hooks';

type Props = {
  item: DroneHarmonyIntegrationItem;
};

const SetupView = (props: Props) => {
  const [droneHarmonyLoginOpened, setDroneHarmonyLoginOpened] = React.useState(false);
  const { reload, isReloading } = useReloadPage();
  const { project } = React.useContext(ProjectContext);
  const { replaceIntegrationItem, removeIntegration, toggleIntegration } =
    React.useContext(IntegrationsContext);
  const { handleError, buildErrorList } =
    useErrorHandling<RequestParamsToApiPaths<CreateDroneHarmonyIntegrationParameters>>();

  // Activating the integration contains two steps:
  // 1. First activating the integration for the project in SkyMap Portal (to get a token to
  //    Drone Harmony.
  // 2. Use the token to authenticate the user in Drone Harmony Portal.
  //
  // If step 2 fails, or is skipped, rerun step 1 next time (and treat conflict error as success).
  const createIntegration = useMutation({
    mutationFn: async () => {
      return SkyMapAxiosServiceFactory.instance
        .createProjectServiceV1()
        .createDroneHarmonyIntegration({
          path: {
            projectId: project.id,
          },
        });
    },
    retry: false,
    networkMode: 'always',
    onError(err) {
      // Ignore error if integration already exists.
      //
      // This can happen when activating the integration a second time, if user decided not to
      // authenticate with Drone Harmony, for instance.
      if (
        errorUtils.isResponseError(
          err,
          'conflict',
          'project:drone_harmony_integration_already_exists',
        )
      ) {
        // When a 409 occurs, we can expect status to be active. If not, for some reason, refresh
        // page.
        if (props.item.status === IntegrationStatus.Active) {
          setDroneHarmonyLoginOpened(true);
          window.open(
            `https://dock.droneharmony.com?at=${props.item.authentication.token}`,
            '_blank',
          );
        } else {
          reload();
        }
      } else {
        handleError(err);
      }
    },
    onSuccess(result) {
      const token = result.data.authentication.token;
      const isSsoLinked = result.data.authentication.isSsoLinked;

      if (isSsoLinked) {
        const value = buildActiveDroneHarmonyIntegrationFromApiResponse(result.data);
        replaceIntegrationItem('drone_harmony', value);
      } else {
        setDroneHarmonyLoginOpened(true);
        window.open(`https://dock.droneharmony.com?at=${token}`, '_blank');
      }
    },
  });

  const removeIntegrationMutation = useRemoveIntegrationQuery({
    projectId: props.item.projectId,
    onError: (err) => {
      handleError(err);
    },
    onMutate: () => {
      toggleIntegration(props.item);
      removeIntegration(props.item);
    },
  });

  return (
    <Stack direction="column" spacing={1}>
      <Stack alignItems="start" direction="row" justifyContent="space-between" spacing={0.5}>
        <IntegrationTitle>{props.item.name}</IntegrationTitle>

        {props.item.status === IntegrationStatus.Active && (
          <Button color="error" variant={'text'} onClick={() => removeIntegrationMutation.mutate()}>
            Ta bort integration
          </Button>
        )}
      </Stack>

      <div>
        <InfoBox
          bottomMargin={true}
          color="yellow"
          leftIcon={{ icon: ['fad', 'info'] }}
          width="fit-content"
        >
          Klicka på knappen nedan för att gå till <strong>Drone Harmony</strong> där du loggar in
          för att aktivera integrationen.
          <br />
          <br />
          Efter att du har loggat in behöver du uppdatera denna sidan.
        </InfoBox>

        {droneHarmonyLoginOpened ? (
          <Button
            color="secondary"
            disabled={isReloading}
            loading={isReloading}
            type="submit"
            variant="contained"
            onClick={reload}
          >
            Uppdatera
          </Button>
        ) : (
          <Button
            color="primary"
            disabled={createIntegration.isPending || isReloading}
            loading={createIntegration.isPending || isReloading}
            type="submit"
            variant="contained"
            onClick={() => {
              createIntegration.mutate();
            }}
          >
            Aktivera
          </Button>
        )}

        {buildErrorList()}
      </div>
    </Stack>
  );
};

export { SetupView };
